import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Card, CardContent } from '../components/ui/card';
import { Gamepad2, Calendar, AlertCircle } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import ArcadeMark from '../components/ArcadeMark';

const PublicShare = () => {
  const { token } = useParams();
  const { API, theme } = useApp(); // On récupère aussi le thème si besoin, mais les classes CSS font le job

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShare = async () => {
      try {
        const response = await axios.get(`${API}/public/shares/${token}`);
        setData(response.data);
      } catch (err) {
        if (err.response?.status === 404) setError("Link not found");
        else if (err.response?.status === 410) setError("This link has expired");
        else setError("An error occurred loading the shared list");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (API) {
        fetchShare();
    }
  }, [token, API]);

  if (loading) {
    return (
      <div className="relative min-h-screen overflow-hidden arcade-field scanlines flex items-center justify-center">
        <div className="relative z-10 w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative min-h-screen overflow-hidden arcade-field scanlines flex items-center justify-center p-4">
        <Alert variant="destructive" className="relative z-10 max-w-md bezel bg-card border-destructive/50 text-destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle className="font-display uppercase tracking-[0.06em]">Error</AlertTitle>
          <AlertDescription className="font-mono text-sm">{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden arcade-field scanlines text-foreground animate-fade-in-up">

      {/* HEADER */}
      <header className="relative z-10 bg-card/80 backdrop-blur border-b border-border sticky top-0">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
             <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-background/60 border border-primary/40 text-primary glow-amber shrink-0">
                <ArcadeMark className="w-6 h-6" />
             </span>
             <div>
                <span className="font-display font-bold text-lg tracking-[0.10em] uppercase block leading-none chrome-text">Arcade</span>
                <span className="eyebrow text-[10px] text-muted-foreground block leading-none mt-1">Fleet</span>
             </div>
          </div>
          <div className="text-xs md:text-sm text-muted-foreground flex items-center gap-2 font-mono bg-background/60 px-3 py-1.5 rounded-full border border-border">
            <Calendar className="w-3.5 h-3.5 text-primary shrink-0" />
            <span>{data.expires_at ? `Valid until ${new Date(data.expires_at).toLocaleDateString()}` : 'No expiration'}</span>
          </div>
        </div>
      </header>

      {/* CONTENT */}
      <main className="relative z-10 container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-display uppercase tracking-[0.06em] sunset-text mb-2">{data.friendly_name}</h1>
          <p className="text-muted-foreground font-mono text-sm">
            <span className="font-display text-primary tabular-nums">{data.cabinets.length}</span> items in this selection
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {data.cabinets.map((cab) => (
            <Card key={cab.unique_id} className="overflow-hidden group bg-card border-border bezel hover:border-primary/50 transition-all duration-300">
              {/* PHOTO */}
              <div className="aspect-[3/4] bg-muted/30 relative overflow-hidden border-b border-border/50">
                {cab.main_photo ? (
                  <img
                    src={cab.main_photo.startsWith('http') ? cab.main_photo : `${API.replace('/api', '')}${cab.main_photo}`}
                    alt={cab.friendly_name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground/30 gap-2">
                    <Gamepad2 className="w-12 h-12" />
                    <span className="text-xs font-mono uppercase">No Image</span>
                  </div>
                )}

                {/* ID Badge en haut à droite */}
                <div className="absolute top-2 right-2">
                  <Badge variant="secondary" className="font-mono text-xs bg-background/80 text-foreground border border-border backdrop-blur-sm shadow-sm">
                    {cab.unique_id}
                  </Badge>
                </div>
              </div>

              {/* DETAILS */}
              <CardContent className="p-4">
                <h3 className="font-heading font-bold text-lg truncate mb-2 tracking-tight" title={cab.friendly_name}>
                  {cab.friendly_name}
                </h3>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="border-border bg-muted/40 uppercase font-mono text-[11px] tracking-wider text-muted-foreground">
                    {cab.cabinet_type || 'Arcade'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      {/* FOOTER */}
      <footer className="relative z-10 py-8 text-center border-t border-border mt-12 bg-card/40">
        <p className="eyebrow text-[10px] text-muted-foreground">
          &copy; {new Date().getFullYear()} Arcade Fleet Manager
        </p>
      </footer>
    </div>
  );
};

export default PublicShare;
